import { create } from 'zustand';
import type { OrdersQueryAPI, OrderUnifiedStatus } from '@hero/api';
import { immer } from 'zustand/middleware/immer';

type OrderQueryStore = {
  /** Current order filters (query params for order list API) */
  filters: OrdersQueryAPI;
  /** Search term (committed via setSearch). Used for input value after debounce. */
  searchTerm: string;
  /** Merge partial query into current filters */
  setFilter: (query: OrdersQueryAPI) => void;
  /** Commit search: sets filters.search (Atlas Search) + searchTerm. Call with debounced value from search bar. */
  setSearch: (term: string) => void;
  /** Clear and set new filters */
  clearAndSetFilter: (query: OrdersQueryAPI) => void;
  /** Reset all order filters and search */
  clearFilter: () => void;
};

export const useOrderQueryStore = create<OrderQueryStore>()(
  immer(set => ({
    filters: {},
    searchTerm: '',
    setFilter: query =>
      set(prev => {
        prev.filters = {
          ...prev.filters,
          ...query,
        };
      }),
    setSearch: term =>
      set(prev => {
        prev.searchTerm = term ?? '';
        prev.filters.search = term || undefined;
      }),
    clearAndSetFilter: query =>
      set(prev => {
        prev.filters = query;
        prev.searchTerm = '';
      }),
    clearFilter: () =>
      set(prev => {
        prev.filters = {};
        prev.searchTerm = '';
      }),
  })),
);

/** Same shape as OrderQueryStore but for active orders (Dashboard Home); keeps search separate from loads table. */
type ActiveOrderStore = {
  /** Current order filters (always includes unifiedStatus: 'any_active_status' for active list). */
  filters: OrdersQueryAPI;
  /** Search term (committed via setSearch). Used for input value after debounce. */
  searchTerm: string;
  /** Merge partial query into current filters (unifiedStatus kept as any_active_status). */
  setFilter: (query: OrdersQueryAPI) => void;
  /** Commit search: sets filters.search (Atlas Search) + searchTerm. Call with debounced value from search bar. */
  setSearch: (term: string) => void;
  /** Clear and set new filters (unifiedStatus kept as any_active_status). */
  clearAndSetFilter: (query: OrdersQueryAPI) => void;
  /** Reset filters and search to default active state. */
  clearFilter: () => void;
};

const ACTIVE_DEFAULT_FILTERS: OrdersQueryAPI = { unifiedStatus: 'any_active_status' };

export const useActiveOrdersStore = create<ActiveOrderStore>()(
  immer(set => ({
    filters: { ...ACTIVE_DEFAULT_FILTERS },
    searchTerm: '',
    setFilter: query =>
      set(prev => {
        prev.filters = {
          ...prev.filters,
          ...query,
          unifiedStatus: 'any_active_status',
        };
      }),
    setSearch: term =>
      set(prev => {
        prev.searchTerm = term ?? '';
        prev.filters.search = term || undefined;
      }),
    clearAndSetFilter: query =>
      set(prev => {
        prev.filters = { ...ACTIVE_DEFAULT_FILTERS, ...query };
        prev.searchTerm = '';
      }),
    clearFilter: () =>
      set(prev => {
        prev.filters = { ...ACTIVE_DEFAULT_FILTERS };
        prev.searchTerm = '';
      }),
  })),
);
